import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import {
  Button,
  Grid,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import { RiderContext } from "../../../context";
import { FilledTextField, Conversion } from "../../../components/FormElements";
import { KeyValueDisplay } from "../../../components/Typography";
import { StringUtils } from "../../../util";
import { isPositiveInt } from "../../../validation/isPositiveInt";
import timeToCurrencyUtil from "../../../util/parsers/timeToCurrencyUtil";
import { useTestIds } from "../../../hooks/useTestIds";

import { If } from "../../../components/Util";
import { withSnackbar } from "../../../HOC/withSnackbar";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  divider: {
    margin: theme.spacing(1, 0, 1, 0),
  },
  formControl: {
    minWidth: 120,
    "& > *": {
      marginRight: theme.spacing(1),
    },
  },
  lineItemRow: {
    marginLeft: `${theme.spacing(1)}px`,
  },
}));

const CreditWallet = ({ setSnackbarMessage, setAlertSeverity }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const testIds = useTestIds("rider-profile-credit-wallet-modal");
  const { t } = useTranslation();
  const {
    reservationCredits,
    fetchReservationCredits,
    creditWallet,
    creditPassTrip,
    receipt,
    fetchReceipt,
    fetchActiveTrip,
    activeTrip,
    activeGroupReservation,
  } = useContext(RiderContext);
  const { reservationId = null, groupReservationId = null } = useParams();

  const [isOpen, setIsOpen] = useState(true);
  const [amount, setAmount] = useState("");
  const [credited, setCredited] = useState(null);
  const [amountCreditable, setAmountCreditable] = useState(null);
  const [amountError, setAmountError] = useState(false);
  const [helperText, setHelperText] = useState(null);
  const [creditPass, setCreditPass] = useState(false);
  const [disableCheckbox, setDisableCheckbox] = useState(false);
  const [convertedOutput, setConvertedOutput] = useState("");
  const [currentReservationId, setCurrentReservationId] = useState(null); // Store either reservationId or groupReservationId

  useEffect(() => {
    setAmount(convertedOutput);
  }, [convertedOutput]);

  useEffect(() => {
    if (reservationId) {
      setCurrentReservationId(reservationId);
    } else {
      setCurrentReservationId(groupReservationId);
    }
  }, [reservationId, groupReservationId]);

  useEffect(() => {
    if (currentReservationId) {
      fetchReceipt(currentReservationId);
      fetchReservationCredits(currentReservationId);
      fetchActiveTrip(currentReservationId);
    }
  }, [currentReservationId]);

  useEffect(() => {
    if (reservationCredits && receipt) {
      const creditedSum = reservationCredits.credited_amount + reservationCredits.credited_bonus;
      setCredited(creditedSum);
      setAmountCreditable(
        (receipt.payments || [])
          .filter(({ status }) => status === "failed" || status === "succeeded")
          .reduce((sum, payment) => sum + payment.totalAmount, 0) - creditedSum,
      );
    }
  }, [receipt, reservationCredits]);

  useEffect(() => {
    if (!receipt?.amount && receipt?.pass_deduction && !creditPass) setAmountError(true);
    else setAmountError(false);
  }, [receipt, creditPass]);

  useEffect(() => {
    setDisableCheckbox(!receipt?.active_pass?.rides_remaining || activeTrip?.pass_use_refunded);
  }, [receipt, activeTrip]);

  useEffect(() => {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) {
      setHelperText("Not a valid number");
      setAmountError(true);
    } else if (numericAmount <= 0) {
      setHelperText("Must be > 0.00");
      setAmountError(true);
    } else if (numericAmount > amountCreditable) {
      setHelperText("Amount must be < creditable");
      setAmountError(true);
    } else {
      setHelperText(null);
      setAmountError(false);
    }
  }, [amount, amountCreditable]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    const promises = [];
    let creditingReservationId = reservationId;
    if (groupReservationId) {
      creditingReservationId = activeGroupReservation.hostReservation.id;
    }
    if (creditPass) promises.push(creditPassTrip(creditingReservationId));
    if (amount) {
      try {
        await creditWallet(amount, creditingReservationId);
      } catch (error) {
        const errors = error.response.data;
        if (errors.credit_amount) {
          setSnackbarMessage(errors.credit_amount[0]);
        } else if (errors.non_field_errors) {
          setSnackbarMessage(errors.non_field_errors[0]);
        }
        setAlertSeverity("error");
      }
    }
    handleClose();
  };

  return (
    <Dialog
      data-testid={testIds.wrapper}
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid={testIds.title}>
        {t("Riders.ReservationDetail.CreditWallet")}
      </DialogTitle>
      <DialogContent data-testid={testIds.content}>
        <Grid container spacing={1}>
          <If condition={receipt?.pass_deduction}>
            <Grid container item xs={12} justify="space-between">
              <Grid item>
                <Typography data-testid={testIds.creditPassTripLabel}>{`${t(
                  "Riders.ReservationDetail.CreditPassTrip",
                )}:`}</Typography>
              </Grid>
              <Grid item>
                <Checkbox
                  checked={creditPass}
                  onChange={(e) => setCreditPass(e.target.checked)}
                  style={{ marginTop: theme.spacing(-1) }}
                  disabled={disableCheckbox}
                  indeterminate={disableCheckbox}
                  data-testid={testIds.creditPassTripCheckbox}
                />
              </Grid>
            </Grid>
            <If condition={activeTrip?.pass_use_refunded}>
              <Grid item xs={12}>
                <Typography data-testid={testIds.passUseRefunded}>
                  {t("Riders.ReservationDetail.PassUseRefunded")}
                </Typography>
              </Grid>
            </If>
            <If condition={receipt?.active_pass?.rides_remaining === null}>
              <Grid item xs={12}>
                <Typography data-testid={testIds.cannotRefundUnlimitedPasses}>
                  {t("Riders.ReservationDetail.CannotRefundUnlimitedPasses")}
                </Typography>
              </Grid>
            </If>
          </If>
          <If condition={receipt?.pass_deduction && receipt?.total_amount}>
            <Grid item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
          </If>
          <If condition={receipt?.total_amount || !receipt?.pass_deduction}>
            <If condition={!receipt?.group_reservation_id}>
              <Grid item xs={12}>
                <KeyValueDisplay
                  label={t("Riders.ReservationDetail.ReservationTotal")}
                  text={formatNumber(receipt?.total_amount, receipt?.rate_plan?.currency)}
                  justify="space-between"
                  testIds={{
                    label: testIds.reservationTotalLabel,
                    text: testIds.reservationTotalValue,
                  }}
                />
              </Grid>
            </If>
            <If condition={receipt?.group_reservation_id}>
              <Grid item xs={12}>
                <KeyValueDisplay
                  label={t("Riders.GroupReservationDetail.GroupTotal")}
                  // Text needed to be updated to show actual total of group reservation
                  text={`${formatNumber(receipt?.total_amount, receipt?.rate_plan?.currency)}`}
                  justify="space-between"
                  separator=""
                  keyTypographyProps={{ variant: "h6" }}
                  valueTypographyProps={{ variant: "h6" }}
                  testIds={{
                    label: testIds.groupTotalLabel,
                    text: testIds.groupTotalValue,
                  }}
                />
              </Grid>
              {receipt?.line_items?.map((lineItem, idx) => (
                <Grid item xs={12} key={lineItem.id}>
                  <KeyValueDisplay
                    label={`${t("Riders.GroupReservationDetail.Ride")} (${
                      lineItem.vehicle_shortcode
                    })`}
                    text={formatNumber(lineItem.total_amount, receipt?.rate_plan?.currency)}
                    className={classes.lineItemRow}
                    justify="space-between"
                    separator=""
                    testIds={{
                      label: `${testIds.itemizedGroupRideVehicle}-${idx}`,
                      text: `${testIds.itemizedGroupRideAmount}-${idx}`,
                    }}
                  />
                </Grid>
              ))}
            </If>
            <Grid item xs={12}>
              <KeyValueDisplay
                label={t("Riders.ReservationDetail.PastCredited")}
                text={formatNumber(credited, receipt?.currencyWallet || receipt?.currencyLocal)}
                justify="space-between"
                testIds={{
                  label: testIds.pastCreditedLabel,
                  text: testIds.pastCreditedValue,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <KeyValueDisplay
                label={t("Riders.ReservationDetail.Creditable")}
                text={formatNumber(
                  amountCreditable,
                  receipt?.currencyWallet || receipt?.currencyLocal,
                )}
                justify="space-between"
                testIds={{
                  label: testIds.creditableLabel,
                  text: testIds.creditableValue,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Conversion
                conversionFunction={(givenInput) =>
                  timeToCurrencyUtil(receipt, givenInput).filledTextReadyAmount
                }
                validateInput={isPositiveInt}
                inputFieldProps={{
                  label: t("Riders.ReservationDetail.Minutes"),
                  errorhelpertext: "Input must be a positive integer",
                }}
                displayOutputField={false}
                exportOutputFunction={setConvertedOutput}
                resetInputOnConvert
                testIds={{
                  convertButton: testIds.convertButton,
                  input: testIds.minutesInput,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FilledTextField
                value={amount}
                mask={receipt?.currencyWallet || receipt?.currencyLocal}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                label={t("Riders.ReservationDetail.CreditAmount")}
                error={amountError}
                helperText={helperText}
                underlineColor="white"
                InputProps={{
                  "data-testid": testIds.creditAmountInput,
                }}
              />
            </Grid>
          </If>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button data-testid={testIds.cancelButton} onClick={handleClose}>
          {t("Riders.Wallet.Cancel")}
        </Button>
        <Button
          data-testid={testIds.saveButton}
          onClick={handleSubmit}
          disabled={amountError || (!amount && !creditPass)}
        >
          {t("Riders.ReservationDetail.CreditWallet")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default withSnackbar(CreditWallet);
CreditWallet.propTypes = {
  setSnackbarMessage: PropTypes.func.isRequired,
  setAlertSeverity: PropTypes.func,
};

CreditWallet.defaultProps = {
  setAlertSeverity: () => {},
};
